package macha.sh.kotlin.components.widgets

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*

// Minimal Markdown renderer covering common blocks + simple inline formatting.
// Supported:
// - Headings: #, ##, ###, ####
// - Paragraphs separated by blank lines
// - Fenced code blocks ```lang ... ``` (lang ignored)
// - Unordered lists (- or *) and ordered lists (1. 2. ...)
// - Inline: `code`, **bold**, *italic*, [text](url)

@Composable
fun SimpleMarkdown(text: String) {
    val lines = text.replace("\r\n", "\n").split("\n")
    var i = 0
    while (i < lines.size) {
        val line = lines[i]
        if (line.isBlank()) { i++; continue }

        // Fenced code block
        if (line.trimStart().startsWith("```") ) {
            val fenceLang = line.trimStart().removePrefix("```").trim()
            i++
            val content = StringBuilder()
            while (i < lines.size && !lines[i].trimStart().startsWith("```") ) {
                content.append(lines[i])
                if (i < lines.size - 1) content.append('\n')
                i++
            }
            // Consume closing fence if present
            if (i < lines.size && lines[i].trimStart().startsWith("```") ) i++
            Pre {
                if (fenceLang.isNotEmpty()) {
                    Code(attrs = { attr("data-lang", fenceLang) }) { Text(content.toString()) }
                } else {
                    Code { Text(content.toString()) }
                }
            }
            continue
        }

        // Heading
        val trimmed = line.trimStart()
        val hashes = trimmed.takeWhile { it == '#' }.length
        if (hashes in 1..4 && trimmed.getOrNull(hashes) == ' ') {
            val title = trimmed.drop(hashes + 1)
            val id = slugify(title)
            when (hashes) {
                1 -> H1(attrs = { id(id) }) { InlineMarkdown(title) }
                2 -> H2(attrs = { id(id) }) { InlineMarkdown(title) }
                3 -> H3(attrs = { id(id) }) { InlineMarkdown(title) }
                else -> H4(attrs = { id(id) }) { InlineMarkdown(title) }
            }
            i++
            continue
        }

        // Horizontal rule (---, ***, ___)
        if (trimmed == "---" || trimmed == "***" || trimmed == "___") {
            Hr {}
            i++
            continue
        }

        // Unordered list
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
            Ul {
                while (i < lines.size) {
                    val l = lines[i].trimStart()
                    if (l.startsWith("- ") || l.startsWith("* ")) {
                        Li { InlineMarkdown(l.drop(2)) }
                        i++
                    } else if (l.isBlank()) { i++; break } else break
                }
            }
            continue
        }

        // Ordered list (e.g., 1. item)
        val olMatch = Regex("^(\\d+)\\. ").find(trimmed)
        if (olMatch != null) {
            Ol {
                while (i < lines.size) {
                    val l = lines[i].trimStart()
                    val m = Regex("^(\\d+)\\. ").find(l)
                    if (m != null) {
                        Li { InlineMarkdown(l.drop(m.value.length)) }
                        i++
                    } else if (l.isBlank()) { i++; break } else break
                }
            }
            continue
        }

        // Blockquote
        if (trimmed.startsWith(">")) {
            // Consume consecutive quote lines
            val content = StringBuilder()
            while (i < lines.size) {
                val l = lines[i]
                val t = l.trimStart()
                if (!t.startsWith(">")) break
                val inner = t.removePrefix(">").trimStart()
                if (content.isNotEmpty()) content.append('\n')
                content.append(inner)
                i++
            }
            // Simple rendering as a styled paragraph
            P({ style { property("border-left", "3px solid #444"); paddingLeft(12.px) } }) { InlineMarkdown(content.toString()) }
            continue
        }

        // Paragraph: collect until blank line or next block
        val para = StringBuilder(line)
        i++
        while (i < lines.size) {
            val l = lines[i]
            val t = l.trimStart()
            if (l.isBlank() || t.startsWith("```") || t.startsWith("#") || t.startsWith(">") || t.startsWith("- ") || t.startsWith("* ") || t == "---" || t == "***" || t == "___" || Regex("^(\\d+)\\. ").containsMatchIn(t)) break
            para.append('\n').append(l)
            i++
        }
        P { InlineMarkdown(para.toString()) }
    }
}

// Generate stable, URL-friendly IDs for heading anchors
private fun slugify(input: String): String {
    val lowered = input.lowercase()
    val sb = StringBuilder(lowered.length)
    var lastDash = false
    for (ch in lowered) {
        when {
            ch.isLetterOrDigit() -> { sb.append(ch); lastDash = false }
            ch.isWhitespace() || ch == '-' -> if (!lastDash) { sb.append('-'); lastDash = true }
            else -> { /* skip */ }
        }
    }
    // Collapse duplicate dashes and trim from ends
    return sb.toString().trim('-')
}

@Composable
private fun InlineMarkdown(text: String) {
    // Process inline code first using backtick sections
    val parts = text.split('`')
    parts.forEachIndexed { idx, part ->
        val isCode = idx % 2 == 1
        if (isCode) {
            Code { Text(part) }
        } else {
            // Inside non-code, handle links and emphasis in a simple pass
            RenderLinksAndEmphasis(part)
        }
    }
}

@Composable
private fun RenderLinksAndEmphasis(text: String) {
    // Manual scan for [label](url) to avoid regex pitfalls on JS/Unicode
    var i = 0
    var last = 0
    while (i < text.length) {
        val open = text.indexOf('[', i)
        if (open == -1) break
        val closeBracket = text.indexOf(']', open + 1)
        if (closeBracket == -1) break
        // Check for image syntax
        val isImage = open > 0 && text[open - 1] == '!'
        val parenIndex = closeBracket + 1
        if (parenIndex >= text.length || text[parenIndex] != '(') {
            i = closeBracket + 1
            continue
        }
        val openParen = parenIndex // at '('
        val closeParen = text.indexOf(')', openParen + 1)
        if (closeParen == -1) break

        val prefixStart = if (isImage) open - 1 else open
        if (prefixStart > last) RenderInlineWithAutolink(text.substring(last, prefixStart))
        val label = text.substring(open + 1, closeBracket)
        val url = text.substring(openParen + 1, closeParen)
        if (isImage) {
            Img(src = url, alt = label)
        } else {
            A(href = url) { RenderBoldItalic(label) }
        }
        i = closeParen + 1
        last = i
    }
    if (last < text.length) RenderInlineWithAutolink(text.substring(last))
}

@Composable
private fun RenderInlineWithAutolink(text: String) {
    var i = 0
    var last = 0
    while (i < text.length) {
        val http = text.indexOf("http://", i)
        val https = text.indexOf("https://", i)
        val start = listOf(http, https).filter { it >= 0 }.minOrNull() ?: break
        if (start > last) RenderBoldItalic(text.substring(last, start))
        var end = start
        while (end < text.length && !text[end].isWhitespace()) end++
        val urlRaw = text.substring(start, end).trimEnd('.', ',', ';', ':', ')')
        A(href = urlRaw) { Text(urlRaw) }
        i = end
        last = i
    }
    if (last < text.length) RenderBoldItalic(text.substring(last))
}

@Composable
private fun RenderBoldItalic(text: String) {
    // Naive handling: ~~strike~~, **bold**, then *italic*
    @Composable
    fun renderStrike(s: String) {
        var i = 0
        while (i < s.length) {
            val start = s.indexOf("~~", i)
            if (start == -1) { Text(s.substring(i)); break }
            val end = s.indexOf("~~", start + 2)
            if (end == -1) { Text(s.substring(i)); break }
            if (start > i) Text(s.substring(i, start))
            Span({ style { property("text-decoration", "line-through") } }) { Text(s.substring(start + 2, end)) }
            i = end + 2
        }
    }
    @Composable
    fun renderItalics(s: String) {
        var i = 0
        while (i < s.length) {
            val start = s.indexOf('*', i)
            if (start == -1) {
                Text(s.substring(i))
                break
            }
            val end = s.indexOf('*', start + 1)
            if (end == -1) {
                Text(s.substring(i))
                break
            }
            if (start > i) Text(s.substring(i, start))
            I { Text(s.substring(start + 1, end)) }
            i = end + 1
        }
    }

    var cursor = 0
    while (cursor < text.length) {
        val start = text.indexOf("**", cursor)
        if (start == -1) {
            renderItalics(text.substring(cursor))
            break
        }
        val end = text.indexOf("**", start + 2)
        if (end == -1) {
            renderItalics(text.substring(cursor))
            break
        }
        if (start > cursor) renderItalics(text.substring(cursor, start))
        B { renderItalics(text.substring(start + 2, end)) }
        cursor = end + 2
    }
}
